/**
 * Bara Bros — About the Brand page
 * File: assets/bb-about-page.js
 *
 * Small vanilla JS only — no React, no Babel, no external libraries.
 * Progressive enhancement: every interactive piece has a meaningful
 * server-rendered default (final stat numbers, face-1 die, "Pull" lever,
 * visible content), so a JS failure or prefers-reduced-motion just means
 * less motion, never missing content.
 *
 * Modules: hero word reel, slot machine + confetti, values die, stat
 * count-up, reveal-on-scroll, light parallax. Re-initializes on
 * `shopify:section:load` so the Theme Editor keeps working after edits.
 */
(function () {
  'use strict';

  if (!document.querySelector) return;

  /* ───────────────────────── motion gating ───────────────────────── */

  function osReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function motionEnabled(root) {
    if (root.getAttribute('data-bba-animations') === 'off') return false;
    var fallback = root.getAttribute('data-bba-reduced-fallback') || 'auto';
    if (fallback === 'always') return false;
    if (fallback === 'never') return true;
    return !osReducedMotion();
  }

  var motionRegistry = [];
  function onMotionChange(root, fn) {
    motionRegistry.push({ root: root, fn: fn });
  }
  function refreshAllMotion() {
    motionRegistry.forEach(function (entry) {
      entry.root.classList.toggle('bb-about--reduced', !motionEnabled(entry.root));
      entry.fn();
    });
  }
  if (window.matchMedia) {
    var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.addEventListener) mq.addEventListener('change', refreshAllMotion);
    else if (mq.addListener) mq.addListener(refreshAllMotion); // Safari < 14
  }

  /* ───────────────────────── hero word reel ───────────────────────── */

  function initHeroReels(root) {
    var reels = root.querySelectorAll('.bb-about-reel');
    for (var n = 0; n < reels.length; n++) {
      (function (reel) {
        if (reel.dataset.bbaReady) return;
        reel.dataset.bbaReady = '1';

        var track = reel.querySelector('.bb-about-reel__track');
        var words = reel.querySelectorAll('.bb-about-reel__word');
        if (!track || words.length < 2) return;

        var speed = parseInt(reel.getAttribute('data-speed'), 10) || 2100;
        var i = 0;
        var timer = null;

        function tick() {
          i = (i + 1) % words.length;
          var h = words[0].getBoundingClientRect().height || reel.getBoundingClientRect().height;
          track.style.transform = 'translateY(' + (-i * h) + 'px)';
        }

        function start() {
          if (timer) clearInterval(timer);
          timer = null;
          if (!motionEnabled(root)) return;
          timer = setInterval(tick, speed);
        }

        start();
        onMotionChange(root, start);
      })(reels[n]);
    }
  }

  /* ───────────────────────── confetti burst ───────────────────────── */

  function fireConfetti() {
    var colors = ['#8B5CFF', '#FF3FD4', '#18D8FF', '#B6FF3B', '#FF2E63', '#FF8A3D', '#ffffff'];
    var layer = document.createElement('div');
    layer.className = 'bb-about-confetti-layer';
    layer.setAttribute('aria-hidden', 'true');

    var frag = document.createDocumentFragment();
    for (var i = 0; i < 110; i++) {
      var bit = document.createElement('span');
      var color = colors[i % colors.length];
      var left = 50 + (Math.random() * 2 - 1) * 26;
      var dx = (Math.random() * 2 - 1) * 64;
      var dy = -(36 + Math.random() * 60);
      var rot = Math.random() * 720 - 360;
      var delay = Math.random() * 0.14;
      var dur = 0.9 + Math.random() * 0.8;
      var size = 6 + Math.random() * 8;
      var round = Math.random() > 0.5;

      bit.className = 'bb-about-confetti-bit' + (round ? ' bb-about-confetti-bit--round' : '');
      bit.style.left = left + '%';
      bit.style.width = size + 'px';
      bit.style.height = size + 'px';
      bit.style.background = color;
      bit.style.boxShadow = '0 0 8px ' + color;
      bit.style.setProperty('--dx', dx + 'vw');
      bit.style.setProperty('--dy', dy + 'vh');
      bit.style.setProperty('--rot', rot + 'deg');
      bit.style.setProperty('--dur', dur + 's');
      bit.style.setProperty('--delay', delay + 's');
      frag.appendChild(bit);
    }
    layer.appendChild(frag);
    document.body.appendChild(layer);
    setTimeout(function () {
      if (layer.parentNode) layer.parentNode.removeChild(layer);
    }, 2200);
  }

  /* ───────────────────────── slot machine ───────────────────────── */

  function initSlotMachines(root) {
    var slots = root.querySelectorAll('.bb-about-slot');
    for (var n = 0; n < slots.length; n++) {
      (function (slot) {
        if (slot.dataset.bbaReady) return;
        slot.dataset.bbaReady = '1';

        var lever = slot.querySelector('.bb-about-slot__lever');
        var leverLabel = lever ? lever.querySelector('.bb-about-slot__lever-label') : null;
        var reels = Array.prototype.slice.call(slot.querySelectorAll('.bb-about-slot__reel'));
        var busy = false;
        var confettiOn = slot.getAttribute('data-confetti') !== 'false';
        var autospinOn = slot.getAttribute('data-autospin') !== 'false';

        function setLabel(text) {
          if (leverLabel) leverLabel.textContent = text;
        }

        function spin() {
          if (busy) return;
          busy = true;
          slot.classList.remove('is-landed');
          setLabel('•••');

          if (lever) {
            lever.classList.add('is-pulled');
            setTimeout(function () { lever.classList.remove('is-pulled'); }, 420);
          }

          var reduced = !motionEnabled(root);

          reels.forEach(function (reel, idx) {
            var strip = reel.querySelector('.bb-about-slot__strip');
            if (!strip) return;
            var cells = strip.querySelectorAll('.bb-about-slot__cell');
            var count = parseInt(reel.getAttribute('data-symbol-count'), 10) || cells.length;
            var loops = parseInt(reel.getAttribute('data-loops'), 10) || Math.max(1, Math.floor(cells.length / Math.max(count, 1)));
            var landIndex = parseInt(reel.getAttribute('data-land-index'), 10) || 0;
            var cellH = reel.clientHeight || 86;
            var restOffset = -(2 * count + landIndex) * cellH;
            var finalOffset = -((loops - 1) * count + landIndex) * cellH;
            var delay = idx * 0.4;

            if (reduced) {
              strip.style.transition = 'none';
              strip.style.transform = 'translateY(' + finalOffset + 'px)';
              return;
            }

            strip.style.transition = 'none';
            strip.style.transform = 'translateY(' + restOffset + 'px)';
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                strip.style.transition = 'transform ' + (1.5 + delay) + 's cubic-bezier(.12,.7,.18,1)';
                strip.style.transform = 'translateY(' + finalOffset + 'px)';
              });
            });
          });

          var land = function () {
            busy = false;
            slot.classList.add('is-landed');
            setLabel('Again');
            if (confettiOn && motionEnabled(root)) fireConfetti();
          };

          if (reduced) {
            setTimeout(land, 60);
          } else {
            setTimeout(land, 2300);
          }
        }

        if (lever) lever.addEventListener('click', spin);

        if (autospinOn && 'IntersectionObserver' in window) {
          var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                io.disconnect();
                setTimeout(spin, 500);
              }
            });
          }, { threshold: 0.45 });
          io.observe(slot);
        }
      })(slots[n]);
    }
  }

  /* ───────────────────────── values die ───────────────────────── */

  var FACE_ROT = [
    { rx: 0, ry: 0 },
    { rx: 0, ry: -90 },
    { rx: 0, ry: 180 },
    { rx: 0, ry: 90 },
    { rx: -90, ry: 0 },
    { rx: 90, ry: 0 }
  ];

  function initValueDice(root) {
    var dices = root.querySelectorAll('.bb-about-dice');
    for (var n = 0; n < dices.length; n++) {
      (function (dice) {
        if (dice.dataset.bbaReady) return;
        dice.dataset.bbaReady = '1';

        var wrap = dice.querySelector('.bb-about-dice__cube-wrap');
        var cube = dice.querySelector('.bb-about-dice__cube');
        var wordEl = dice.querySelector('.bb-about-dice__word');
        var blurbEl = dice.querySelector('.bb-about-dice__blurb');
        var dots = Array.prototype.slice.call(dice.querySelectorAll('.bb-about-dice__dot'));
        if (!wrap || !cube || !dots.length) return;

        var autorotate = dice.getAttribute('data-autorotate') !== 'false';
        var clickroll = dice.getAttribute('data-clickroll') !== 'false';
        var active = parseInt(dice.getAttribute('data-active'), 10) || 0;
        var turns = 0;
        var hovering = false;
        var timer = null;

        function apply(idx, extraSpin) {
          var target = FACE_ROT[idx];
          turns = turns + 1 + (extraSpin || 0);
          var ry = target.ry + 360 * turns;
          var rx = target.rx;

          if (!motionEnabled(root)) cube.style.transition = 'none';
          else cube.style.transition = '';
          cube.style.transform = 'rotateX(' + (rx - 16) + 'deg) rotateY(' + (ry - 16) + 'deg)';

          active = idx;
          dice.setAttribute('data-active', String(idx));

          var dot = dots[idx];
          if (dot) {
            var color = dot.getAttribute('data-color');
            if (wordEl) {
              wordEl.textContent = dot.getAttribute('data-word') || '';
              wordEl.style.textShadow = color ? ('0 0 18px ' + color + '66') : '';
            }
            if (blurbEl) blurbEl.textContent = dot.getAttribute('data-blurb') || '';
          }
          dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
        }

        if (clickroll) {
          wrap.setAttribute('role', 'button');
          wrap.setAttribute('tabindex', '0');
          wrap.setAttribute('aria-label', 'Roll the values die');
          wrap.addEventListener('click', function () {
            var next = Math.floor(Math.random() * 6);
            apply(next, 2 + Math.floor(Math.random() * 2));
          });
          wrap.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
              e.preventDefault();
              var next = Math.floor(Math.random() * 6);
              apply(next, 2 + Math.floor(Math.random() * 2));
            }
          });
        }

        dots.forEach(function (dot, i) {
          dot.addEventListener('click', function () { apply(i, 0); });
        });

        wrap.addEventListener('mouseenter', function () { hovering = true; });
        wrap.addEventListener('mouseleave', function () { hovering = false; });

        function startAuto() {
          if (timer) clearInterval(timer);
          timer = null;
          if (!autorotate || !motionEnabled(root)) return;
          timer = setInterval(function () {
            if (hovering) return;
            apply((active + 1) % 6, 0);
          }, 3200);
        }

        startAuto();
        onMotionChange(root, startAuto);
      })(dices[n]);
    }
  }

  /* ───────────────────────── stat count-up ───────────────────────── */

  function initStatsCounters(root) {
    var values = root.querySelectorAll('.bb-about-stats__value[data-countup]');
    if (!values.length) return;

    function animate(el) {
      var target = parseFloat(el.getAttribute('data-target')) || 0;
      var decimals = parseInt(el.getAttribute('data-decimals'), 10) || 0;
      var numEl = el.querySelector('.bb-about-stats__num');
      if (!numEl) return;

      if (!motionEnabled(root)) {
        numEl.textContent = target.toFixed(decimals);
        return;
      }

      var dur = 1500;
      var start = null;
      function tick(ts) {
        if (!start) start = ts;
        var p = Math.min(1, (ts - start) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        numEl.textContent = (target * eased).toFixed(decimals);
        if (p < 1) requestAnimationFrame(tick);
        else numEl.textContent = target.toFixed(decimals);
      }
      requestAnimationFrame(tick);
    }

    var pending = [];
    for (var n = 0; n < values.length; n++) {
      var el = values[n];
      if (el.dataset.bbaReady) continue;
      el.dataset.bbaReady = '1';
      if (el.getAttribute('data-countup') === 'false') continue;
      pending.push(el);
    }
    if (!pending.length) return;

    if (!('IntersectionObserver' in window)) {
      pending.forEach(animate);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        animate(entry.target);
      });
    }, { threshold: 0.4 });
    pending.forEach(function (el) { io.observe(el); });
  }

  /* ───────────────────────── reveal-on-scroll ───────────────────────── */

  function initReveal(root) {
    var els = root.querySelectorAll('[data-bba-reveal]');
    if (!els.length) return;

    var fresh = [];
    for (var n = 0; n < els.length; n++) {
      var el = els[n];
      if (el.dataset.bbaArmed) continue;
      el.dataset.bbaArmed = '1';
      el.classList.add('bba-armed');
      fresh.push(el);
    }
    if (!fresh.length) return;

    if (!('IntersectionObserver' in window)) {
      fresh.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.18 });
    fresh.forEach(function (el) { io.observe(el); });
  }

  /* ───────────────────────── light parallax ───────────────────────── */

  function initParallax(root) {
    if (root.dataset.bbaParallaxReady) return;
    root.dataset.bbaParallaxReady = '1';

    var ticking = false;

    function apply() {
      if (!motionEnabled(root)) return;
      var els = root.querySelectorAll('[data-parallax-speed]');
      if (!els.length) return;
      var vh = window.innerHeight;
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        var speed = parseFloat(el.getAttribute('data-parallax-speed')) || 0;
        if (!speed) continue;
        var r = el.getBoundingClientRect();
        var center = r.top + r.height / 2 - vh / 2;
        var off = Math.max(-240, Math.min(240, -center * speed));
        el.style.transform = 'translate3d(0,' + off.toFixed(1) + 'px,0)';
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; apply(); });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    apply();
  }

  /* ───────────────────────── bootstrap ───────────────────────── */

  function initInstance(root) {
    root.classList.toggle('bb-about--reduced', !motionEnabled(root));
    initHeroReels(root);
    initSlotMachines(root);
    initValueDice(root);
    initStatsCounters(root);
    initReveal(root);
    initParallax(root);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('.bb-about');
    for (var i = 0; i < roots.length; i++) initInstance(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Theme Editor: re-initialize the section whose DOM was just replaced.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
